import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useConfirmModal } from '../../context/ConfirmModalContext';
import { useTranslations } from '../../hooks/useTranslations';
import { Project, ProjectStatus, Lead, Payment } from '../../types';
import ContextMenu from '../ui/ContextMenu';
import ActionIconButton from '../ui/ActionIconButton';
import { appendEditDeleteMenuItems } from '../ui/tableActions';
import {
  Clock,
  Edit,
  Trash2,
  Shield,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Plus,
  Paperclip,
  ExternalLink,
  ArrowRight,
  DollarSign,
  Radio,
  Link2,
  HardDrive,
  FolderOpen,
} from 'lucide-react';
import { clickableLinks, paymentStatusColors } from '../../styles/colors';
import { filesService } from '../../services/files';
import { googleDriveService, DriveDestinations } from '../../services/googleDrive';
import { supabaseData, checkQuotaLimit } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import { useQuota } from '../../context/QuotaContext';
import { getNextProjectStatus } from '../../utils/statusUtils';
import { formatRightsDurationLabel } from '../../utils/rightsDuration';
import { isOptimisticId } from '../../utils/optimisticId';
import RightsEndStatus from './RightsEndStatus';
import ProjectTypeIcon from '../ui/ProjectTypeIcon';
import Tooltip from '../ui/Tooltip';

/** Delay (ms) during which we ignore a click after a file drop to avoid opening edit modal (browsers may fire click after drop). */
const CLICK_IGNORE_AFTER_DROP_MS = 300;

type ProjectDocument = {
  id: string;
  file_name: string;
  created_at?: string;
  source?: 'leadsme' | 'drive' | string | null;
  drive_web_view_link?: string | null;
};

const PROJECT_DOCS_SELECT =
  'id, created_at, file_name, source, drive_web_view_link';

interface ProjectCardProps {
  project: Project;
  lead?: Lead;
  payment?: Payment;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onUploadFile: (project: Project) => void;
  onMarkComplete: (project: Project) => void;
  onCreatePayment?: (project: Project) => void;
  onViewPayment?: (payment: Payment) => void;
  onMarkPaymentAsPaid?: (payment: Payment) => void;
  onViewLead?: (leadId: string) => void;
  onMoveToNextStatus?: (project: Project) => void;
  onStartDiffusion?: (project: Project) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  lead,
  payment,
  onEdit,
  onDelete,
  onUploadFile,
  onMarkComplete,
  onCreatePayment,
  onViewPayment,
  onMarkPaymentAsPaid,
  onViewLead,
  onMoveToNextStatus,
  onStartDiffusion,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { formatDateShort, formatDateCompact, formatCurrency } = useTranslations();
  const { showToast } = useToast();
  const { confirm } = useConfirmModal();
  const { showQuotaLimit } = useQuota();
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isLinkingFromDrive, setIsLinkingFromDrive] = useState(false);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  /** Ignore the next click after a file drop to avoid opening the edit modal (browsers often fire click after drop). */
  const ignoreNextClickRef = useRef(false);

  // Track multiple file uploads with progress
  interface UploadProgress {
    file: File;
    status: 'pending' | 'uploading' | 'success' | 'error';
    progress?: number;
    error?: string;
    fileId?: string;
  }
  const [uploadQueue, setUploadQueue] = useState<UploadProgress[]>([]);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [loadingUrls, setLoadingUrls] = useState<Set<string>>(new Set());
  const [driveExportQueue, setDriveExportQueue] = useState<
    Array<{ fileId: string; fileName: string; status: 'exporting' | 'success' | 'error' }>
  >([]);
  const [driveDestinations, setDriveDestinations] = useState<DriveDestinations | null>(null);

  useEffect(() => {
    let isCancelled = false;

    async function loadDocuments() {
      if (!user) return;
      if (isOptimisticId(project.id)) return;

      try {
        const { data, error } = await supabaseData
          .from('files')
          .select(PROJECT_DOCS_SELECT)
          .eq('owner_user_id', user.id)
          .eq('entity_type', 'projects')
          .eq('entity_id', project.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading project documents:', error);
          return;
        }

        if (!isCancelled && data) {
          setDocuments(data as ProjectDocument[]);
        }
      } catch (err) {
        console.error('Exception while loading project documents:', err);
      }
    }

    loadDocuments();

    return () => {
      isCancelled = true;
    };
  }, [project.id, user]);

  const openDocument = async (fileId: string) => {
    const doc = documents.find((d) => d.id === fileId);
    if (doc?.source === 'drive') {
      await openDocumentInDrive(fileId);
      return;
    }

    setLoadingUrls((prev) => new Set(prev).add(fileId));
    // Open sync so the browser allows the tab even after a cold-start wait
    const popup = window.open('about:blank', '_blank');
    try {
      const viewUrl = await filesService.getViewUrl(fileId);
      if (popup && !popup.closed) {
        popup.opener = null;
        popup.location.replace(viewUrl);
      } else {
        window.open(viewUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error opening document:', error);
      if (popup && !popup.closed) popup.close();
      showToast(t('projects.documentOpenError') || "Erreur lors de l'ouverture du document", 'error');
    } finally {
      setLoadingUrls((prev) => {
        const updated = new Set(prev);
        updated.delete(fileId);
        return updated;
      });
    }
  };

  const openDocumentInDrive = async (fileId: string) => {
    setLoadingUrls((prev) => new Set(prev).add(fileId));
    try {
      const result = await googleDriveService.resolveFile(fileId);
      if (!result.available) {
        if (result.deleted) {
          setDocuments((prev) => prev.filter((row) => row.id !== fileId));
          showToast(t('projects.documentDriveLinkMissing'), 'warning');
        } else {
          setDocuments((prev) =>
            prev.map((row) =>
              row.id === fileId ? { ...row, drive_web_view_link: null } : row
            )
          );
          showToast(t('projects.documentDriveMissing'), 'warning');
        }
        return;
      }
      if (!result.webViewLink) {
        showToast(t('projects.documentDriveOpenError'), 'error');
        return;
      }
      window.open(result.webViewLink, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('Error opening document in Drive:', error);
      if (error?.status === 401) {
        showToast(t('projects.documentDriveNotConnected'), 'error');
        navigate('/app/settings?tab=integrations&gdrive=connect');
        return;
      }
      showToast(t('projects.documentDriveOpenError'), 'error');
    } finally {
      setLoadingUrls((prev) => {
        const updated = new Set(prev);
        updated.delete(fileId);
        return updated;
      });
    }
  };

  const linkDocumentFromDrive = async () => {
    if (!user) {
      showToast(t('common.notAuthenticated') || 'Tu dois être connecté.', 'error');
      return;
    }
    if (isOptimisticId(project.id)) {
      showToast(t('projects.saveProjectFirst'), 'warning');
      return;
    }
    if (isLinkingFromDrive) return;

    ignoreNextClickRef.current = true;
    window.setTimeout(() => {
      ignoreNextClickRef.current = false;
    }, 400);

    setIsLinkingFromDrive(true);
    try {
      if (driveDestinations && driveDestinations.pickerConfigured === false) {
        showToast(t('projects.documentDrivePickerNotConfigured'), 'warning');
        return;
      }
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
      const picked = await googleDriveService.pickFile(t('projects.chooseDriveFile'));
      if (!picked) return;

      const result = await googleDriveService.linkFile(project.id, picked.id);
      const linked = result.file;
      setDocuments((prev) => {
        if (prev.some((row) => row.id === linked.id)) return prev;
        return [
          {
            id: linked.id,
            file_name: linked.file_name,
            created_at: linked.created_at || new Date().toISOString(),
            source: (linked.source as ProjectDocument['source']) || 'drive',
            drive_web_view_link: linked.drive_web_view_link || null,
          },
          ...prev,
        ];
      });
      showToast(
        result.alreadyLinked
          ? t('projects.documentAlreadyLinkedFromDrive', { name: linked.file_name })
          : t('projects.documentLinkedFromDrive', { name: linked.file_name }),
        'success'
      );
    } catch (error: any) {
      console.error('Drive file link error:', error);
      if (error?.status === 401) {
        showToast(t('projects.documentDriveNotConnected'), 'error');
        navigate('/app/settings?tab=integrations&gdrive=connect');
        return;
      }
      if (error?.code === 'PICKER_NOT_CONFIGURED' || error?.status === 503) {
        showToast(t('projects.documentDrivePickerNotConfigured'), 'warning');
        return;
      }
      if (error?.code === 'DRIVE_FILE_TYPE_NOT_ALLOWED') {
        showToast(t('projects.invalidFileType'), 'error');
        return;
      }
      if (error?.code === 'DRIVE_FILE_INVALID' || error?.code === 'DRIVE_FILE_FORBIDDEN') {
        showToast(t('projects.documentDriveLinkInaccessible'), 'error');
        return;
      }
      if (error?.code === 'DRIVE_LINK_SCHEMA') {
        showToast(t('projects.documentDriveLinkSchemaError'), 'error');
        return;
      }
      showToast(t('projects.documentDriveLinkError'), 'error');
    } finally {
      setIsLinkingFromDrive(false);
    }
  };

  const refreshDriveDestinations = async () => {
    try {
      const destinations = await googleDriveService.getDestinations();
      setDriveDestinations(destinations);
    } catch {
      // Not connected or API error — menu falls back to Leadsme-only send
      setDriveDestinations(null);
    }
  };

  const sendDocumentToDrive = async (fileId: string, folderId: string) => {
    const doc = documents.find((d) => d.id === fileId);
    const fileName = doc?.file_name || t('projects.document');
    setDriveExportQueue((prev) => [
      ...prev.filter((item) => item.fileId !== fileId),
      { fileId, fileName, status: 'exporting' },
    ]);
    try {
      const result = await googleDriveService.exportFile(fileId, folderId);
      setDocuments((prev) =>
        prev.map((row) =>
          row.id === fileId ? { ...row, drive_web_view_link: result.webViewLink } : row
        )
      );
      setDriveExportQueue((prev) =>
        prev.map((item) =>
          item.fileId === fileId ? { ...item, status: 'success' } : item
        )
      );
      showToast(t('projects.documentSentToDrive', { name: fileName }), 'success');
      void refreshDriveDestinations();
      window.setTimeout(() => {
        setDriveExportQueue((prev) => prev.filter((item) => item.fileId !== fileId));
      }, 1800);
    } catch (error: any) {
      console.error('Error exporting document to Drive:', error);
      setDriveExportQueue((prev) =>
        prev.map((item) =>
          item.fileId === fileId ? { ...item, status: 'error' } : item
        )
      );
      window.setTimeout(() => {
        setDriveExportQueue((prev) => prev.filter((item) => item.fileId !== fileId));
      }, 3500);
      if (error?.status === 401) {
        showToast(t('projects.documentDriveNotConnected'), 'error');
        navigate('/app/settings?tab=integrations&gdrive=connect');
        return;
      }
      showToast(t('projects.documentDriveExportError'), 'error');
    }
  };

  const chooseDriveFolderAndSend = async (fileId: string) => {
    // Menu closes first; ignore the click falling through onto the project card.
    ignoreNextClickRef.current = true;
    window.setTimeout(() => {
      ignoreNextClickRef.current = false;
    }, 400);

    try {
      if (driveDestinations && driveDestinations.pickerConfigured === false) {
        showToast(t('projects.documentDrivePickerNotConfigured'), 'warning');
        return;
      }
      // Let the ContextMenu portal unmount before opening the Google overlay.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
      const folder = await googleDriveService.pickFolder(t('projects.chooseDriveFolder'));
      if (!folder) return;
      await sendDocumentToDrive(fileId, folder.id);
    } catch (error: any) {
      console.error('Drive folder picker error:', error);
      if (error?.status === 401) {
        showToast(t('projects.documentDriveNotConnected'), 'error');
        navigate('/app/settings?tab=integrations&gdrive=connect');
        return;
      }
      if (error?.code === 'PICKER_NOT_CONFIGURED' || error?.status === 503) {
        showToast(t('projects.documentDrivePickerNotConfigured'), 'warning');
        return;
      }
      showToast(t('projects.documentDrivePickerError'), 'error');
    }
  };

  const pickFiles = (onPick: (files: File[]) => void) => {
    const input = fileInputRef.current;
    if (!input) return;

    input.value = '';
    input.onchange = () => {
      const files = input.files ? Array.from(input.files) : [];
      if (files.length === 0) return;
      onPick(files);
    };
    input.click();
  };

  const uploadSingleFile = async (file: File, updateProgress: (progress: UploadProgress) => void): Promise<void> => {
    if (!user) {
      throw new Error(t('common.notAuthenticated') || 'Tu dois être connecté pour uploader un fichier.');
    }
    if (isOptimisticId(project.id)) {
      showToast(t('projects.saveProjectFirst'), 'warning');
      return;
    }

    updateProgress({ file, status: 'uploading', progress: 0 });

    try {
      const { fileId, uploadUrl } = await filesService.createUploadUrl({
        entityType: 'projects',
        entityId: project.id,
        ownerUserId: user.id,
        file,
      });

      updateProgress({ file, status: 'uploading', progress: 50, fileId });

      await filesService.uploadFileToSignedUrl(uploadUrl, file);
      await filesService.confirmUpload(fileId);

      updateProgress({ file, status: 'success', progress: 100, fileId });

      // Add document to list
      setDocuments((prev) => [{ id: fileId, file_name: file.name, created_at: new Date().toISOString() }, ...prev]);

      // Remove this successful file from queue after 1.5 seconds (using fileId for precise identification)
      setTimeout(() => {
        setUploadQueue(prev => prev.filter(u => u.fileId !== fileId));
      }, 1500);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const errorMessage = error?.error === 'QUOTA_LIMIT'
        ? (error?.quotaInfo ? 'QUOTA_LIMIT' : t('projects.quotaExceeded') || 'Quota dépassé')
        : (error?.message || t('projects.documentUploadError') || "Erreur lors de l'upload du document");
      
      updateProgress({ file, status: 'error', error: errorMessage });
      throw error;
    }
  };

  const uploadMultipleFiles = async (files: File[]) => {
    if (!user) {
      showToast(t('common.notAuthenticated') || 'Tu dois être connecté pour uploader un fichier.', 'error');
      return;
    }
    if (isOptimisticId(project.id)) {
      showToast(t('projects.saveProjectFirst'), 'warning');
      return;
    }

    if (files.length === 0) return;

    // Standard SaaS: Validate file types - PDF and common image formats allowed for project documents
    const allowedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
    ];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif'];
    
    const invalidFiles = files.filter(file => {
      const lowerName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => lowerName.endsWith(ext));
      const hasValidMimeType = allowedMimeTypes.includes(file.type);
      return !hasValidExtension && !hasValidMimeType;
    });

    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ');
      showToast(
        `${t('projects.invalidFileType') || 'Seuls les fichiers PDF et images (JPG, PNG, WEBP, GIF) sont autorisés pour les documents de projet.'} ${invalidFiles.length > 1 ? `Fichiers rejetés : ${invalidNames}` : `Fichier rejeté : ${invalidNames}`}`,
        'error'
      );
      // Filter out invalid files and continue with valid ones
      const validFiles = files.filter(file => {
        const lowerName = file.name.toLowerCase();
        const hasValidExtension = allowedExtensions.some(ext => lowerName.endsWith(ext));
        const hasValidMimeType = allowedMimeTypes.includes(file.type);
        return hasValidExtension || hasValidMimeType;
      });
      if (validFiles.length === 0) return;
      files = validFiles;
    }

    // Initialize upload queue immediately for instant feedback
    const initialQueue: UploadProgress[] = files.map(file => ({
      file,
      status: 'pending' as const,
    }));
    setUploadQueue(initialQueue);
    setIsUploadingDocument(true);

    // Validate total size in bytes (same unit as File.size / quota)
    const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
    const quotaCheck = await checkQuotaLimit('storage', files.length, totalSizeBytes);
    if (!quotaCheck.allowed) {
      showQuotaLimit(quotaCheck);
      setUploadQueue([]);
      setIsUploadingDocument(false);
      return;
    }

    // Upload files sequentially
    let successCount = 0;
    let errorCount = 0;
    let quotaLimitReached = false;
    const successfulFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      if (quotaLimitReached) break;

      const file = files[i];
      const updateProgress = (progress: UploadProgress) => {
        setUploadQueue(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], ...progress };
          return updated;
        });
      };

      try {
        await uploadSingleFile(file, updateProgress);
        successCount++;
        successfulFiles.push(file.name);
      } catch (error: any) {
        errorCount++;
        if (error?.error === 'QUOTA_LIMIT') {
          quotaLimitReached = true;
          showQuotaLimit(error.quotaInfo);
        }
      }
    }

    setIsUploadingDocument(false);

    // Show summary toast with file names
    if (successCount > 0 && errorCount === 0) {
      const fileNames = successfulFiles.length <= 2 
        ? successfulFiles.join(', ')
        : `${successfulFiles.slice(0, 2).join(', ')} +${successfulFiles.length - 2}`;
      showToast(
        t('projects.fileUploadedSuccess', { name: fileNames }) || 
        `${fileNames} uploadé avec succès`,
        'success'
      );
    } else if (successCount > 0 && errorCount > 0) {
      showToast(
        t('projects.filesUploadedPartial', { success: successCount, error: errorCount }) || 
        `${successCount} fichier${successCount > 1 ? 's' : ''} uploadé${successCount > 1 ? 's' : ''}, ${errorCount} erreur${errorCount > 1 ? 's' : ''}`,
        'warning'
      );
    }

    // Clear remaining items (errors and pending) after 5 seconds
    // Successful files are already removed individually after 1.5s
    setTimeout(() => {
      setUploadQueue(prev => prev.filter(u => u.status === 'error' || u.status === 'pending'));
    }, 5000);
  };

  const handleUploadNewDocument = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isUploadingDocument) return;

    // input.click() bubbles to the card and would open the edit modal
    ignoreNextClickRef.current = true;
    setTimeout(() => { ignoreNextClickRef.current = false; }, CLICK_IGNORE_AFTER_DROP_MS);

    pickFiles((files) => uploadMultipleFiles(files));
  };

  // File drag & drop : dépôt de fichiers uniquement (Kanban gère le drag des cartes projet).
  const handleFileDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsFileDragOver(true);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the card area (not just moving to a child)
    const target = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!target.contains(relatedTarget)) {
      setIsFileDragOver(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragOver(false);

    if (!e.dataTransfer.types.includes('Files')) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      ignoreNextClickRef.current = true;
      setTimeout(() => { ignoreNextClickRef.current = false; }, CLICK_IGNORE_AFTER_DROP_MS);
      uploadMultipleFiles(files);
    }
  };

  // Delete document with confirmation via global modal
  const handleDeleteDocument = async (fileId: string) => {
    if (!fileId) return;

    const documentToDelete = documents.find(doc => doc.id === fileId);
    if (!documentToDelete) return;

    const fileName = documentToDelete.file_name || documentToDelete.id;

    // Show confirmation modal via global context
    const isDriveLink = documentToDelete.source === 'drive';
    const confirmed = await confirm({
      title: isDriveLink
        ? t('projects.unlinkDocumentTitle')
        : t('projects.deleteDocumentTitle') || 'Supprimer le document',
      message: isDriveLink
        ? t('projects.unlinkDocumentConfirm', { name: fileName })
        : t('projects.deleteDocumentConfirm', { name: fileName }) ||
          `Tu es sûr de vouloir supprimer "${fileName}" ? Cette action est irréversible.`,
      confirmText: isDriveLink ? t('projects.unlinkDocument') : t('common.delete'),
      variant: 'danger',
    });

    if (!confirmed) return;

    // Optimistic UI: remove document immediately
    setDocuments((prev) => prev.filter((doc) => doc.id !== fileId));

    // Delete in background (Drive link-only = DB unlink; S3 skipped server-side)
    filesService.deleteFile(fileId).then(() => {
      showToast(
        isDriveLink
          ? t('projects.documentUnlinked', { name: fileName })
          : t('projects.documentDeleted', { name: fileName }),
        'success'
      );
    }).catch((error) => {
      console.error('Error deleting document file:', error);
      // Rollback: refetch documents
      if (user) {
        supabaseData
          .from('files')
          .select(PROJECT_DOCS_SELECT)
          .eq('owner_user_id', user.id)
          .eq('entity_type', 'projects')
          .eq('entity_id', project.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .then(({ data }) => {
            if (data) {
              setDocuments(data as ProjectDocument[]);
            }
          });
      }
      showToast(
        t('projects.documentDeleteError') || 'Erreur lors de la suppression du document',
        'error'
      );
    });
  };



  const handleCardActivate = () => {
    if (ignoreNextClickRef.current) return;
    onEdit(project);
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-label={`${t('common.edit')} ${project.name}`}
      className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 h-full flex flex-col relative overflow-hidden cursor-pointer ${
        isFileDragOver
          ? 'scale-[1.02] shadow-md ring-2 ring-primary-500/30'
          : 'border border-gray-200'
      }`}
      onClick={handleCardActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardActivate();
        }
      }}
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {/* File drop overlay - Modern minimal style */}
      {isFileDragOver && (
        <div className="absolute inset-0 z-20 pointer-events-none rounded-xl">
          {/* Semi-transparent backdrop */}
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl" />
          
          {/* Border glow */}
          <div className="absolute inset-0 rounded-xl ring-2 ring-primary-500 ring-offset-2 ring-offset-white" />
          
          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-3 px-4 py-2.5 bg-primary-500 text-white rounded-full shadow-lg shadow-primary-500/40">
              <Paperclip className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t('projects.dropFilesHere') || 'Dépose tes fichiers'}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="p-4 md:p-5 lg:p-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-2 md:mb-3 lg:mb-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1 min-w-0">
              <ProjectTypeIcon type={project.type} variant="avatar" />
              <h3 className="text-base lg:text-lg font-bold text-gray-900 truncate min-w-0">{project.name}</h3>
            </div>
            
            {lead && (
              <div className="text-sm text-gray-600 flex items-center min-w-0 gap-1">
                <span className="shrink-0">{t('projects.lead')}:</span>
                {onViewLead ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewLead(lead.id);
                    }}
                    className={`font-semibold ${clickableLinks} hover:underline truncate max-w-full inline-block text-left`}
                  >
                    {lead.name}
                  </button>
                ) : (
                  <span className="font-semibold text-gray-700 truncate min-w-0">{lead.name}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Brief + Droits de cession sur une ligne (gain de hauteur) - hidden on mobile */}
        <div className="hidden md:flex md:items-center md:justify-between md:gap-3">
          <div className="min-w-0 flex-1">
            {project.brief ? (
              <p className="text-sm text-gray-600 truncate">
                {project.brief}
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic truncate">
                {t('projects.noBriefMessage')}
              </p>
            )}
          </div>
          {project.rights && (
            <div className="flex items-center gap-3 shrink-0">
              {project.rights.endDate ? (
                <RightsEndStatus
                  endDate={project.rights.endDate}
                  checkedAt={project.rights.checkedAt}
                  compact
                />
              ) : (
                <Tooltip content={t('projects.rightsPlannedTooltip')}>
                  <div className="inline-flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Radio className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-medium">
                      {formatRightsDurationLabel(project.rights.duration, t)}
                    </span>
                  </div>
                </Tooltip>
              )}
              {project.rights.exclusivity && (
                <div className="flex items-center space-x-1">
                  <Shield className="w-3 h-3 text-purple-600" />
                  <span className="inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs text-white bg-purple-500">
                    {t('projects.imageRightsBadge')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Payment — hairline with equal space above/below the rule */}
        <div className="border-t border-gray-200 mt-2.5 pt-2.5 md:mt-3 md:pt-3">
          {/* Metrics - Amount, status, payment info */}
          <div className="flex items-center justify-between min-h-[28px]">
            <div className="flex items-center flex-wrap gap-2 min-w-0">
              {payment ? (
                <>
                  {/* Montant du paiement (source de vérité) ; repli sur le montant projet */}
                  {typeof payment.amount === 'number' ? (
                    <span className="text-base lg:text-lg font-semibold text-gray-900">
                      {formatCurrency(
                        payment.amount,
                        payment.currency || project.currency || 'EUR'
                      )}
                    </span>
                  ) : typeof project.amount === 'number' ? (
                    <span className="text-base lg:text-lg font-semibold text-gray-900">
                      {formatCurrency(project.amount, project.currency || 'EUR')}
                    </span>
                  ) : null}
                  <span className={`inline-flex items-center font-medium rounded-full px-2 py-0.5 text-xs ${paymentStatusColors[payment.status].badge}`}>
                    {payment.status === 'sent' ? t('payments.statusLabels.sent') : payment.status === 'paid' ? t('projects.paid') : t('projects.overdue')}
                  </span>
                </>
              ) : typeof project.amount === 'number' && project.amount > 0 ? (
                /* Montant prévu — bouton pour associer un paiement */
                <span className="inline-flex items-center gap-0.5 min-w-0">
                  <span className="text-base lg:text-lg font-semibold text-gray-900">
                    {formatCurrency(project.amount, project.currency || 'EUR')}
                  </span>
                  {onCreatePayment && !isOptimisticId(project.id) && (
                    <ActionIconButton
                      icon={Link2}
                      onClick={() => onCreatePayment(project)}
                      variant="ghost"
                      size="sm"
                      tooltip={t('projects.trackPayment')}
                    />
                  )}
                </span>
              ) : onCreatePayment && !isOptimisticId(project.id) ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreatePayment(project);
                  }}
                  className={`text-sm ${clickableLinks} hover:underline shrink-0`}
                >
                  {t('projects.addPayment')}
                </button>
              ) : null}
            </div>
            
            {/* Payment date - hidden on mobile */}
            <div className="hidden md:flex items-center space-x-1.5">
              {payment && (
                <Tooltip
                  content={
                    payment.status === 'paid' && payment.paymentDate
                      ? t('payments.paymentDate')
                      : t('payments.dueDate')
                  }
                >
                  <div className="inline-flex items-center space-x-1.5">
                    <DollarSign className={`w-3.5 h-3.5 ${payment.status === 'paid' ? 'text-green-600' : payment.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'}`} />
                    <span
                      className={`text-sm tabular-nums ${payment.status === 'paid' ? 'text-green-600' : payment.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'} font-medium`}
                    >
                      {payment.status === 'paid' && payment.paymentDate
                        ? formatDateCompact(payment.paymentDate)
                        : formatDateCompact(payment.dueDate)
                      }
                    </span>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          {/* Spacer below payment section */}
          <div className="mb-2 md:mb-3 lg:mb-5"></div>
        </div>

        {/* Upload Progress List - Modern compact style */}
        {uploadQueue.length > 0 && (
          <div className="mb-2 md:mb-3 lg:mb-5 space-y-1.5">
            {uploadQueue.map((upload, index) => (
              <div
                key={`${upload.file.name}-${index}`}
                className={`relative overflow-hidden rounded-lg text-xs transition-all duration-300 ${
                  upload.status === 'success' 
                    ? 'bg-emerald-50 border border-emerald-200/60' 
                    : upload.status === 'error'
                    ? 'bg-red-50 border border-red-200/60'
                    : 'bg-slate-50 border border-slate-200/60'
                }`}
              >
                {/* Progress bar background */}
                {upload.status === 'uploading' && (
                  <div 
                    className="absolute inset-0 bg-gradient-to-r from-primary-100 to-primary-50 transition-all duration-300"
                    style={{ width: `${upload.progress || 0}%` }}
                  />
                )}
                
                {/* Content */}
                <div className="relative flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {upload.status === 'uploading' && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <Loader2 className="w-3 h-3 animate-spin text-white" />
                      </div>
                    )}
                    {upload.status === 'success' && (
                      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {upload.status === 'error' && (
                      <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {upload.status === 'pending' && (
                      <div className="w-5 h-5 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span className={`truncate font-medium flex-1 min-w-0 ${
                      upload.status === 'success' ? 'text-emerald-700' :
                      upload.status === 'error' ? 'text-red-700' :
                      'text-slate-700'
                    }`}>
                      {upload.file.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {upload.status === 'uploading' && upload.progress !== undefined && (
                      <span className="text-primary-600 font-semibold tabular-nums">{upload.progress}%</span>
                    )}
                    {upload.status === 'error' && (
                      <Tooltip
                        content={
                          upload.error === 'QUOTA_LIMIT'
                            ? t('projects.quotaExceeded')
                            : upload.error || t('common.error')
                        }
                      >
                        <span className="text-red-600 font-medium truncate max-w-[80px]">
                          {upload.error === 'QUOTA_LIMIT' ? t('projects.quotaExceeded') : t('common.error')}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drive export progress — same compact pattern as uploads */}
        {driveExportQueue.length > 0 && (
          <div className="mb-2 md:mb-3 lg:mb-5 space-y-1.5">
            {driveExportQueue.map((item) => (
              <div
                key={`drive-export-${item.fileId}`}
                className={`relative overflow-hidden rounded-lg text-xs transition-all duration-300 ${
                  item.status === 'success'
                    ? 'bg-emerald-50 border border-emerald-200/60'
                    : item.status === 'error'
                      ? 'bg-red-50 border border-red-200/60'
                      : 'bg-slate-50 border border-slate-200/60'
                }`}
              >
                <div className="relative flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {item.status === 'exporting' && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <Loader2 className="w-3 h-3 animate-spin text-white" />
                      </div>
                    )}
                    {item.status === 'success' && (
                      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {item.status === 'error' && (
                      <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <span
                      className={`truncate font-medium flex-1 min-w-0 ${
                        item.status === 'success'
                          ? 'text-emerald-700'
                          : item.status === 'error'
                            ? 'text-red-700'
                            : 'text-slate-700'
                      }`}
                    >
                      {item.fileName}
                    </span>
                  </div>
                  {item.status === 'error' && (
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-red-600 font-medium">{t('common.error')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Spacer to push footer to bottom */}
        <div className="flex-1"></div>

        {/* Footer with custom background */}
        <div
          className="flex items-center justify-between py-2.5 lg:py-3 rounded-b-lg -mx-4 md:-mx-5 lg:-mx-6 -mb-4 md:-mb-5 lg:-mb-6 px-4 md:px-5 lg:px-6"
          style={{ backgroundColor: '#f8fafc' }}
        >
          {/* Date info - date only on mobile, icon+date on md+ */}
          <div className="flex items-center space-x-1.5 text-xs text-gray-500 min-w-0">
            <Clock className="w-3 h-3 flex-shrink-0 hidden md:block" />
            <span className="truncate">{formatDateShort(project.updatedAt)}</span>
          </div>

          <div className="flex items-center space-x-1">
            {/* Documents: Paperclip menu if none, FileText menu if some */}
            {documents.length === 0 ? (
              <ContextMenu
                items={[
                  {
                    label: t('projects.addDocument'),
                    icon: isUploadingDocument ? Loader2 : Paperclip,
                    onClick: handleUploadNewDocument,
                    disabled: isUploadingDocument || isLinkingFromDrive || !user,
                  },
                  {
                    label: isLinkingFromDrive
                      ? t('projects.openingDrivePicker')
                      : t('projects.linkDocumentFromDrive'),
                    icon: isLinkingFromDrive ? Loader2 : HardDrive,
                    onClick: () => void linkDocumentFromDrive(),
                    disabled: isLinkingFromDrive || !user,
                  },
                ]}
                trigger="more"
                size="sm"
                onOpenChange={(open) => {
                  if (open) void refreshDriveDestinations();
                }}
                triggerIcon={
                  isUploadingDocument || isLinkingFromDrive ? Loader2 : Paperclip
                }
                triggerIconClassName={
                  isUploadingDocument || isLinkingFromDrive ? 'animate-spin' : ''
                }
                tooltip={
                  isLinkingFromDrive
                    ? t('projects.openingDrivePicker')
                    : t('projects.addDocumentTooltip') || t('projects.addDocument')
                }
              />
            ) : (
              <ContextMenu
                items={[
                  {
                    label: t('projects.addDocument'),
                    icon: Paperclip,
                    onClick: handleUploadNewDocument,
                    disabled: isUploadingDocument || isLinkingFromDrive || !user,
                  },
                  {
                    label: isLinkingFromDrive
                      ? t('projects.openingDrivePicker')
                      : t('projects.linkDocumentFromDrive'),
                    icon: isLinkingFromDrive ? Loader2 : HardDrive,
                    onClick: () => void linkDocumentFromDrive(),
                    disabled: isLinkingFromDrive || !user,
                  },
                  ...documents.map((doc) => {
                    const isUrlLoading = loadingUrls.has(doc.id);
                    const isDriveSource = doc.source === 'drive';
                    const isExporting = driveExportQueue.some(
                      (item) => item.fileId === doc.id && item.status === 'exporting'
                    );
                    const driveLink = doc.drive_web_view_link || null;
                    const recentFolders = driveDestinations?.recentFolders || [];
                    const driveSendChildren = [
                      {
                        label: t('projects.chooseDriveFolder'),
                        icon: FolderOpen,
                        disabled: isExporting,
                        onClick: () => chooseDriveFolderAndSend(doc.id),
                      },
                      ...recentFolders.map((folder) => ({
                        label: folder.name,
                        icon: isExporting ? Loader2 : FolderOpen,
                        disabled: isExporting,
                        onClick: () => sendDocumentToDrive(doc.id, folder.id),
                      })),
                    ];
                    const driveChildren = isDriveSource
                      ? []
                      : [
                          ...(driveLink
                            ? [
                                {
                                  label: t('projects.openDocumentInDrive'),
                                  icon: isUrlLoading ? Loader2 : ExternalLink,
                                  disabled: isUrlLoading,
                                  onClick: () => openDocumentInDrive(doc.id),
                                },
                              ]
                            : []),
                          recentFolders.length === 0
                            ? {
                                label: t('projects.sendDocumentToDrive'),
                                icon: isExporting ? Loader2 : HardDrive,
                                disabled: isExporting,
                                onClick: () => chooseDriveFolderAndSend(doc.id),
                              }
                            : {
                                label: t('projects.sendDocumentToDrive'),
                                icon: isExporting ? Loader2 : HardDrive,
                                disabled: isExporting,
                                children: driveSendChildren,
                              },
                        ];

                    return {
                      label: doc.file_name || t('projects.document') || 'Document',
                      icon: isDriveSource ? HardDrive : FileText,
                      children: [
                        {
                          label: t('projects.viewDocument') || 'Voir',
                          icon: isUrlLoading ? Loader2 : ExternalLink,
                          disabled: isUrlLoading,
                          onClick: () => openDocument(doc.id),
                        },
                        ...driveChildren,
                        {
                          label: isDriveSource
                            ? t('projects.unlinkDocument')
                            : t('projects.deleteDocument') || 'Supprimer',
                          icon: isDriveSource ? Link2 : Trash2,
                          onClick: () => handleDeleteDocument(doc.id),
                        },
                      ],
                    };
                  }),
                ]}
                trigger="more"
                size="sm"
                onOpenChange={(open) => {
                  if (open) void refreshDriveDestinations();
                }}
                triggerIcon={
                  isUploadingDocument || isLinkingFromDrive || loadingUrls.size > 0
                    ? Loader2
                    : FileText
                }
                triggerIconClassName={
                  isUploadingDocument || isLinkingFromDrive || loadingUrls.size > 0
                    ? 'animate-spin'
                    : ''
                }
                tooltip={
                  isLinkingFromDrive
                    ? t('projects.openingDrivePicker')
                    : loadingUrls.size > 0
                      ? t('projects.openingDocument')
                      : t('projects.documents') || 'Documents'
                }
              />
            )}
            
            {/* More actions */}
            <ContextMenu
              contextTargetRef={cardRef}
              items={(() => {
                const items: Array<{ label: string; onClick: (e?: React.MouseEvent) => void; icon?: any; disabled?: boolean }> = [];
                
                // 1. Move to next status action
                if (onMoveToNextStatus) {
                  const nextStatus = getNextProjectStatus(project.status);
                  if (nextStatus) {
                    const statusToTranslationKey: Record<ProjectStatus, string> = {
                      'BRIEF_RECEIVED': 'projects.statusBriefReceived',
                      'IN_PRODUCTION': 'projects.statusInProduction',
                      'V1_SENT': 'projects.statusFirstVersion',
                      'CHANGES_REQUESTED': 'projects.statusCorrections',
                      'DELIVERED': 'projects.statusDelivered',
                      'ARCHIVED': 'projects.statusArchived',
                    };
                    const nextStatusLabel = t(statusToTranslationKey[nextStatus] || `projects.status${nextStatus}`);
                    items.push({
                      label: `${t('projects.moveTo')} ${nextStatusLabel}`,
                      onClick: (e) => {
                        e?.stopPropagation();
                        onMoveToNextStatus(project);
                      },
                      icon: ArrowRight,
                    });
                  }
                }

                // 2. Payment actions
                if (payment) {
                  if (onViewPayment) {
                    items.push({
                      label: t('projects.viewPayment'),
                      onClick: (e) => {
                        e?.stopPropagation();
                        onViewPayment(payment);
                      },
                      icon: ExternalLink,
                    });
                  }
                  if (onMarkPaymentAsPaid && payment.status !== 'paid') {
                    items.push({
                      label: t('payments.markAsPaid'),
                      onClick: (e) => {
                        e?.stopPropagation();
                        onMarkPaymentAsPaid(payment);
                      },
                      icon: DollarSign,
                    });
                  }
                } else if (onCreatePayment && !isOptimisticId(project.id)) {
                  const hasExpectedAmount =
                    typeof project.amount === 'number' && project.amount > 0;
                  items.push({
                    label: hasExpectedAmount
                      ? t('projects.trackPayment')
                      : t('projects.addPayment'),
                    onClick: (e) => {
                      e?.stopPropagation();
                      onCreatePayment(project);
                    },
                    icon: hasExpectedAmount ? Link2 : Plus,
                  });
                }

                // 3. Image rights (activate or edit)
                if (onStartDiffusion && !isOptimisticId(project.id) && project.rights) {
                  items.push({
                    label: project.rights.startDate
                      ? t('projects.editImageRights')
                      : t('projects.activateImageRights'),
                    onClick: (e) => {
                      e?.stopPropagation();
                      onStartDiffusion(project);
                    },
                    icon: Radio,
                  });
                }

                appendEditDeleteMenuItems({
                  items,
                  t,
                  onEdit: () => onEdit(project),
                  onDelete: () => onDelete(project.id),
                  omitFromMenu: true,
                });

                return items;
              })()}
              trigger="more"
              size="sm"
              tooltip={t('common.moreActions')}
            />
            
            <ActionIconButton
              icon={Edit}
              onClick={(e) => {
                e?.stopPropagation();
                onEdit(project);
              }}
              variant="edit"
              size="sm"
              tooltip={t('common.edit')}
            />
            <ActionIconButton
              icon={Trash2}
              onClick={(e) => {
                e?.stopPropagation();
                onDelete(project.id);
              }}
              variant="delete"
              size="sm"
              tooltip={t('common.delete')}
            />
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf,.jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

export default ProjectCard;
